import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { ChatMessage } from '../../../../types';
import RealTimeMonitor from './RealTimeMonitor';
import { useLocalization } from '../../../../hooks/useLocalization';
import { socketService } from '../../../../services/socketService';

interface SessionFromAPI {
    _id: string;
    sessionId: string;
    userId: string;
    userName?: string;
    userEmail?: string;
    status: string;
    messages: ChatMessage[];
    lastUpdated: number;
    unreadByAdmin: number;
    language?: string;
    createdAt?: string;
}

// Local labels for admin Support module
const labels: Record<string, Record<string, string>> = {
    en: {
        title: 'Support Center',
        subtitle: 'Monitor and respond to customer chat sessions',
        refresh: 'Refresh',
        chats: 'Chats',
        live_traffic: 'Live Traffic',
        total_sessions: 'Total Sessions',
        waiting_for_agent: 'Waiting for Agent',
        agent_active: 'Agent Active',
        ai_handled: 'AI Handled',
        all: 'All',
        waiting: 'Waiting',
        active: 'Active',
        ai_label: 'AI',
        closed: 'Closed',
        loading_sessions: 'Loading sessions...',
        no_sessions: 'No chat sessions found',
        no_messages: 'No messages',
        msgs: 'msgs',
        select_conversation: 'Select a conversation',
        select_hint: 'Choose a chat session from the list to view messages and respond',
        messages_label: 'messages',
        customer: 'Customer',
        you_agent: 'You (Agent)',
        ai_bot: 'AI Bot',
        reply_placeholder: 'Type your message... (Enter to send, Shift+Enter for new line)',
        reply_placeholder_mobile: 'Type your reply...',
        send: 'Send Reply',
        sending: 'Sending...',
        session_info: 'Session Info',
        user: 'User',
        email: 'Email',
        status: 'Status',
        language_label: 'Language',
        messages_count: 'Messages',
        created: 'Created',
        reply: 'Reply',
        take_over: 'Take Over',
        join_as_agent: 'Join as Agent',
        close_session: 'Close Session',
        delete: 'Delete',
        session_closed: 'This session is closed',
        delete_title: 'Delete Session?',
        delete_message: 'This will permanently delete this chat session and all its messages. This action cannot be undone.',
        cancel: 'Cancel',
        // Status labels
        status_ai: 'AI Bot',
        status_waiting: 'Waiting',
        status_active: 'Active',
        status_closed: 'Closed',
    },
    sv: {
        title: 'Supportcenter',
        subtitle: 'Övervaka och svara på kundchattsessioner',
        refresh: 'Uppdatera',
        chats: 'Chattar',
        live_traffic: 'Realtidstrafik',
        total_sessions: 'Totalt Sessioner',
        waiting_for_agent: 'Väntar på Agent',
        agent_active: 'Agent Aktiv',
        ai_handled: 'AI Hanterat',
        all: 'Alla',
        waiting: 'Väntar',
        active: 'Aktiv',
        ai_label: 'AI',
        closed: 'Stängd',
        loading_sessions: 'Laddar sessioner...',
        no_sessions: 'Inga chattsessioner hittades',
        no_messages: 'Inga meddelanden',
        msgs: 'msg',
        select_conversation: 'Välj en konversation',
        select_hint: 'Välj en chattsession från listan för att visa meddelanden och svara',
        messages_label: 'meddelanden',
        customer: 'Kund',
        you_agent: 'Du (Agent)',
        ai_bot: 'AI Bot',
        reply_placeholder: 'Skriv ditt meddelande... (Enter för att skicka, Shift+Enter för ny rad)',
        reply_placeholder_mobile: 'Skriv ditt svar...',
        send: 'Skicka Svar',
        sending: 'Skickar...',
        session_info: 'Sessionsinfo',
        user: 'Användare',
        email: 'E-post',
        status: 'Status',
        language_label: 'Språk',
        messages_count: 'Meddelanden',
        created: 'Skapad',
        reply: 'Svar',
        take_over: 'Ta Över',
        join_as_agent: 'Gå med som Agent',
        close_session: 'Stäng Session',
        delete: 'Radera',
        session_closed: 'Den här sessionen är stängd',
        delete_title: 'Radera Session?',
        delete_message: 'Detta kommer att permanent radera denna chattsession och alla dess meddelanden. Åtgärden kan inte ångras.',
        cancel: 'Avbryt',
        status_ai: 'AI Bot',
        status_waiting: 'Väntar',
        status_active: 'Aktiv',
        status_closed: 'Stängd',
    }
};

const SupportDashboard: React.FC = () => {
    const { language } = useLocalization();
    const l = labels[language] || labels.en;

    const [sessions, setSessions] = useState<SessionFromAPI[]>([]);
    const [selectedSession, setSelectedSession] = useState<SessionFromAPI | null>(null);
    const [replyText, setReplyText] = useState('');
    const [filter, setFilter] = useState<string>('all');
    const [view, setView] = useState<'chat' | 'traffic'>('chat');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const didScrollRef = useRef(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const fetchSessions = useCallback(async () => {
        try {
            const res = await fetch('/api/chat/admin/sessions?limit=100');
            const data = await res.json();
            if (data.success && data.sessions) {
                setSessions(data.sessions);
                if (selectedSession) {
                    const updated = data.sessions.find((s: SessionFromAPI) => s.sessionId === selectedSession.sessionId);
                    if (updated) setSelectedSession(updated);
                }
                setError(null);
            } else {
                setError('Failed to load sessions');
            }
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
            setError('Connection error.');
        } finally {
            setLoading(false);
        }
    }, [selectedSession?.sessionId]);

    useEffect(() => {
        fetchSessions();
        const interval = setInterval(fetchSessions, 15000);

        socketService.connect();
        socketService.registerAdmin();
        socketService.onSessionsUpdate((updatedSessions) => {
            setSessions(updatedSessions);
        });

        return () => {
            clearInterval(interval);
            socketService.off('sessions_update');
        };
    }, []);

    // Keep selected session in sync with real-time updates
    useEffect(() => {
        if (selectedSession) {
            const updated = sessions.find(s => s.sessionId === selectedSession.sessionId);
            if (updated && updated.lastUpdated !== selectedSession.lastUpdated) {
                setSelectedSession(updated);
            }
        }
    }, [sessions, selectedSession?.sessionId, selectedSession?.lastUpdated]);

    useEffect(() => {
        if (selectedSession) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [selectedSession?.sessionId, selectedSession?.messages?.length]);

    useEffect(() => {
        if (selectedSession && selectedSession.status !== 'closed') {
            setTimeout(() => textareaRef.current?.focus(), 200);
        }
    }, [selectedSession?.sessionId]);

    const filteredSessions = sessions.filter(s => {
        if (filter === 'all') return true;
        if (filter === 'waiting') return s.status === 'waiting_for_agent';
        if (filter === 'active') return s.status === 'agent_active';
        if (filter === 'ai') return s.status === 'ai';
        if (filter === 'closed') return s.status === 'closed';
        return true;
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ai': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'waiting_for_agent': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
            case 'agent_active': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'closed': return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'ai': return l.status_ai;
            case 'waiting_for_agent': return l.status_waiting;
            case 'agent_active': return l.status_active;
            case 'closed': return l.status_closed;
            default: return status;
        }
    };

    const handleSelectSession = async (session: SessionFromAPI) => {
        didScrollRef.current = false;
        setSelectedSession(session);
        setReplyText('');
        socketService.joinAdminSession(session.sessionId);
        try {
            await fetch(`/api/chat/admin/session/${session.sessionId}/mark-read`, { method: 'POST' });
            setSessions(prev => prev.map(s =>
                s.sessionId === session.sessionId ? { ...s, unreadByAdmin: 0 } : s
            ));
            socketService.markRead(session.sessionId);
        } catch (e) {
            console.error('Mark read error:', e);
        }
    };

    const handleSendReply = async () => {
        if (!replyText.trim() || !selectedSession || sending) return;
        setSending(true);
        setError(null);
        try {
            const res = await fetch(`/api/chat/admin/session/${selectedSession.sessionId}/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: replyText.trim(), agentName: 'Admin Support' })
            });
            const data = await res.json();
            if (data.success) {
                setReplyText('');
                if (data.session) {
                    setSelectedSession(data.session);
                    setSessions(prev => prev.map(s =>
                        s.sessionId === data.session.sessionId ? data.session : s
                    ));
                }
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            } else {
                setError(data.message || data.error || 'Failed to send reply');
            }
        } catch (err: any) {
            setError(`Failed: ${err.message || 'Connection error'}`);
        } finally {
            setSending(false);
        }
    };

    const handleDeleteSession = async (sessionId: string) => {
        try {
            const res = await fetch(`/api/chat/admin/session/${sessionId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
                if (selectedSession?.sessionId === sessionId) setSelectedSession(null);
                setDeleteConfirm(null);
            } else {
                setError(data.message || 'Failed to delete session');
            }
        } catch (err) {
            setError('Failed to delete session');
        }
    };

    const handleCloseSession = async (sessionId: string) => {
        try {
            const res = await fetch(`/api/chat/admin/session/${sessionId}/close`, { method: 'POST' });
            const data = await res.json();
            if (data.success && data.session) {
                setSelectedSession(data.session);
                setSessions(prev => prev.map(s => s.sessionId === sessionId ? data.session : s));
            }
        } catch (err) {
            console.error('Close session error:', err);
        }
    };

    const handleJoinAsAgent = async () => {
        if (!selectedSession) return;
        try {
            await fetch(`/api/chat/admin/session/${selectedSession.sessionId}/assign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ agentName: 'Admin Support' })
            });
            await fetchSessions();
        } catch (err) {
            console.error('Join error:', err);
        }
    };

    const handleStartChat = (userId: string, _socketId: string) => {
        const existing = sessions.find(s => s.userId === userId);
        if (existing) {
            didScrollRef.current = false;
            setSelectedSession(existing);
        }
        setView('chat');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendReply();
        }
    };

    const stats = {
        total: sessions.length,
        waiting: sessions.filter(s => s.status === 'waiting_for_agent').length,
        active: sessions.filter(s => s.status === 'agent_active').length,
        aiHandled: sessions.filter(s => s.status === 'ai').length
    };

    return (
        <div className="flex-1 overflow-hidden flex flex-col bg-gray-50 dark:bg-gray-900 absolute md:static inset-0 z-10 w-full h-full">
            {/* Header */}
            <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{l.title}</h1>
                        <p className="text-gray-500 dark:text-gray-400">{l.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => fetchSessions()}
                            className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-xs font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
                            <span className={`material-icons-round text-sm ${loading ? 'animate-spin' : ''}`}>refresh</span>
                            {l.refresh}
                        </button>
                        <div className="flex bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                            <button onClick={() => setView('chat')}
                                className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${view === 'chat' ? 'bg-white text-blue-600 shadow' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'}`}>
                                {l.chats}
                            </button>
                            <button onClick={() => setView('traffic')}
                                className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${view === 'traffic' ? 'bg-white text-green-600 shadow' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'}`}>
                                {l.live_traffic}
                            </button>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
                        <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
                        <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                            <span className="material-icons-round text-sm">close</span>
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">{l.total_sessions}</p>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-xl border border-orange-200 dark:border-orange-800">
                        <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.waiting}</p>
                        <p className="text-xs text-orange-600 dark:text-orange-400 uppercase font-semibold">{l.waiting_for_agent}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl border border-green-200 dark:border-green-800">
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.active}</p>
                        <p className="text-xs text-green-600 dark:text-green-400 uppercase font-semibold">{l.agent_active}</p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.aiHandled}</p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 uppercase font-semibold">{l.ai_handled}</p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden relative">
                {view === 'traffic' ? (
                    <div className="w-full p-6"><RealTimeMonitor onStartChat={handleStartChat} /></div>
                ) : (
                    <>
                        {/* Session List */}
                        <div className={`w-full md:w-72 lg:w-80 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col overflow-hidden absolute inset-0 z-10 md:relative md:z-0 transition-transform duration-300 ${selectedSession ? '-translate-x-full md:translate-x-0' : 'translate-x-0'}`}>
                            <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex gap-2 overflow-x-auto">
                                {[
                                    { id: 'all', label: l.all },
                                    { id: 'waiting', label: l.waiting },
                                    { id: 'active', label: l.active },
                                    { id: 'ai', label: l.ai_label },
                                    { id: 'closed', label: l.closed }
                                ].map(f => (
                                    <button key={f.id} onClick={() => setFilter(f.id)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${filter === f.id
                                            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}>
                                        {f.label}
                                    </button>
                                ))}
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                {loading ? (
                                    <div className="p-6 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                                        <p className="text-sm text-gray-500">{l.loading_sessions}</p>
                                    </div>
                                ) : filteredSessions.length === 0 ? (
                                    <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                                        <span className="material-icons-round text-4xl mb-2 text-gray-300 dark:text-gray-600">chat_bubble_outline</span>
                                        <p className="text-sm">{l.no_sessions}</p>
                                    </div>
                                ) : (
                                    filteredSessions.map(session => (
                                        <div key={session.sessionId}
                                            className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors group relative ${selectedSession?.sessionId === session.sessionId
                                                ? 'bg-primary/5 border-l-4 border-l-primary'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                                            <div onClick={() => handleSelectSession(session)} className="w-full">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                                            {(session.userId || 'USER').substring(0, 2).toUpperCase()}
                                                        </div>
                                                        <span className="font-medium text-gray-900 dark:text-white text-sm truncate">{session.userName || session.userId}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                        {session.status === 'waiting_for_agent' && (
                                                            <span className="relative flex h-2 w-2">
                                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                                                            </span>
                                                        )}
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusColor(session.status)}`}>
                                                            {getStatusLabel(session.status)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                    {session.messages?.[session.messages.length - 1]?.text?.substring(0, 50) || l.no_messages}
                                                </p>
                                                <div className="flex justify-between items-center mt-2">
                                                    <span className="text-[10px] text-gray-400">
                                                        {session.messages?.length || 0} {l.msgs} • {new Date(session.lastUpdated).toLocaleString()}
                                                    </span>
                                                    {(session.unreadByAdmin || 0) > 0 && (
                                                        <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                                            {session.unreadByAdmin}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(session.sessionId); }}
                                                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                                                title={l.delete}>
                                                <span className="material-icons-round text-sm">delete_outline</span>
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Chat + Reply — Outlook Style */}
                        <div className={`flex-1 flex overflow-hidden w-full absolute inset-0 z-20 bg-gray-50 dark:bg-gray-900 md:relative md:z-0 transition-transform duration-300 ${selectedSession ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}`}>
                            {selectedSession ? (
                                <div className="flex-1 flex flex-col md:flex-row overflow-hidden absolute inset-0 md:static w-full h-full">
                                    {/* Messages Pane */}
                                    <div className="flex-1 flex flex-col overflow-hidden min-w-0 h-full">
                                        <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex justify-between items-center">
                                            <div className="flex items-center gap-3">
                                                <button onClick={() => setSelectedSession(null)} className="md:hidden mr-2 text-gray-500">
                                                    <span className="material-icons-round">arrow_back</span>
                                                </button>
                                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center text-white font-bold text-sm">
                                                    {(selectedSession.userId || 'U').substring(0, 2).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 dark:text-white text-sm">{selectedSession.userName || selectedSession.userId}</p>
                                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                        {selectedSession.messages?.length || 0} {l.messages_label} • {selectedSession.language || 'sv'}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${getStatusColor(selectedSession.status)}`}>
                                                {getStatusLabel(selectedSession.status)}
                                            </span>
                                        </div>

                                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
                                            {(selectedSession.messages || []).filter(m => m.messageType !== 'system').map((msg, i) => {
                                                const isUser = msg.sender === 'user';
                                                const isAdmin = msg.sender === 'admin';
                                                return (
                                                    <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                                        <div className={`max-w-[85%] md:max-w-[80%] rounded-2xl px-4 py-2 text-sm ${isUser
                                                            ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-br-none'
                                                            : isAdmin
                                                                ? 'bg-green-600 text-white rounded-bl-none'
                                                                : 'bg-blue-600 text-white rounded-bl-none'}`}>
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="text-[10px] font-bold opacity-70 uppercase">
                                                                    {isUser ? l.customer : isAdmin ? l.you_agent : l.ai_bot}
                                                                </span>
                                                                <span className="text-[10px] opacity-50 ml-4">
                                                                    {new Date(msg.timestamp).toLocaleTimeString()}
                                                                </span>
                                                            </div>
                                                            <p className="whitespace-pre-wrap">{msg.text}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div ref={messagesEndRef} />
                                        </div>

                                        {/* Mobile Reply */}
                                        {selectedSession.status !== 'closed' && (
                                            <div className="md:hidden p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex gap-2">
                                                <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSendReply()}
                                                    placeholder={l.reply_placeholder_mobile} disabled={sending}
                                                    className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20" />
                                                <button onClick={handleSendReply} disabled={!replyText.trim() || sending}
                                                    className="px-4 py-2 bg-primary text-white rounded-xl font-bold text-sm disabled:opacity-50">
                                                    <span className="material-icons-round text-base">send</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Reply + Info Pane (Desktop) */}
                                    <div className="hidden md:flex md:w-80 lg:w-96 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-col overflow-hidden">
                                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                            <h3 className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold mb-3">{l.session_info}</h3>
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500 dark:text-gray-400">{l.user}</span>
                                                    <span className="font-medium text-gray-900 dark:text-white">{selectedSession.userName || selectedSession.userId}</span>
                                                </div>
                                                {selectedSession.userEmail && (
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-500 dark:text-gray-400">{l.email}</span>
                                                        <span className="font-medium text-gray-900 dark:text-white text-xs">{selectedSession.userEmail}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500 dark:text-gray-400">{l.status}</span>
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusColor(selectedSession.status)}`}>
                                                        {getStatusLabel(selectedSession.status)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500 dark:text-gray-400">{l.language_label}</span>
                                                    <span className="text-gray-900 dark:text-white">{selectedSession.language?.toUpperCase() || 'SV'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500 dark:text-gray-400">{l.messages_count}</span>
                                                    <span className="text-gray-900 dark:text-white">{selectedSession.messages?.length || 0}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500 dark:text-gray-400">{l.created}</span>
                                                    <span className="text-xs text-gray-900 dark:text-white">{selectedSession.createdAt ? new Date(selectedSession.createdAt).toLocaleString() : '-'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-2">
                                            {(selectedSession.status === 'waiting_for_agent' || selectedSession.status === 'ai') && (
                                                <button onClick={handleJoinAsAgent}
                                                    className="w-full px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 flex items-center justify-center gap-2 transition-all shadow-md">
                                                    <span className="material-icons-round text-lg">headset_mic</span>
                                                    {selectedSession.status === 'ai' ? l.take_over : l.join_as_agent}
                                                </button>
                                            )}
                                            {selectedSession.status !== 'closed' && (
                                                <button onClick={() => handleCloseSession(selectedSession.sessionId)}
                                                    className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center justify-center gap-1 transition-all">
                                                    <span className="material-icons-round text-sm">close</span>
                                                    {l.close_session}
                                                </button>
                                            )}
                                            <button onClick={() => setDeleteConfirm(selectedSession.sessionId)}
                                                className="w-full px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center gap-1 transition-all">
                                                <span className="material-icons-round text-sm">delete</span>
                                                {l.delete}
                                            </button>
                                        </div>

                                        {selectedSession.status !== 'closed' ? (
                                            <div className="flex-1 flex flex-col p-4">
                                                <h3 className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold mb-2 flex items-center gap-1.5">
                                                    <span className="material-icons-round text-sm">reply</span>
                                                    {l.reply}
                                                </h3>
                                                <textarea ref={textareaRef} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                                                    onKeyDown={handleKeyDown} placeholder={l.reply_placeholder} disabled={sending} rows={6}
                                                    className="flex-1 min-h-[120px] w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-gray-900 dark:text-white text-sm resize-none disabled:opacity-50" />
                                                <button onClick={handleSendReply} disabled={!replyText.trim() || sending}
                                                    className="mt-3 w-full px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all">
                                                    <span className={`material-icons-round ${sending ? 'animate-spin' : ''}`}>
                                                        {sending ? 'hourglass_empty' : 'send'}
                                                    </span>
                                                    {sending ? l.sending : l.send}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex-1 flex items-center justify-center p-4">
                                                <div className="text-center text-gray-500 dark:text-gray-400">
                                                    <span className="material-icons-round text-3xl mb-2 text-gray-300 dark:text-gray-600">lock</span>
                                                    <p className="text-sm">{l.session_closed}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-center text-gray-500 dark:text-gray-400">
                                    <div>
                                        <span className="material-icons-round text-6xl mb-4 text-gray-300 dark:text-gray-600">forum</span>
                                        <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300">{l.select_conversation}</h3>
                                        <p className="text-sm">{l.select_hint}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-red-500">warning</span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{l.delete_title}</h3>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{l.delete_message}</p>
                        <div className="flex gap-3">
                            <button onClick={() => setDeleteConfirm(null)}
                                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
                                {l.cancel}
                            </button>
                            <button onClick={() => handleDeleteSession(deleteConfirm)}
                                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all flex items-center justify-center gap-2">
                                <span className="material-icons-round text-sm">delete</span>
                                {l.delete}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupportDashboard;
